import React from 'react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import Layout from '@components/layout';
import Helmet from '@basic/Helmet';
import Hero from '../components/homepage/Hero';
import IconsA from '@solutions/icons-aV2';
import Section from '@basic/layouts/Section';
import LargeScreenshotAccordion from '@basic/accordion/LargeScreenshotAccordion';
import ImageBlockContainer from '@basic/image-block/AlternatingStackedImageBlocks';
import { Cell, Grid } from '@basic/layouts/Grid';
import Text from '@basic/Text';
import CTA from '@components/basic/BasicCTA';
import Carousel from '@components/Slider/CarouselV2';
import Takeover from '@components/homepage/Takeover';
import Button from '@basic/BasicButton';

import theme from '../styles/theme';
import { Overline } from '@basic/layouts/Section';
import BasicTestimonial from '@basic/BasicTestimonial';

const {
	font,
	colors,
	breakPoints: { mobileFirstScreenQuery },
} = theme;

const QuoteCell = styled(Cell)`
	background: linear-gradient(
		45deg,
		rgba(219, 229, 249, 1) 4%,
		rgba(231, 227, 238, 1) 14%,
		rgba(219, 229, 249, 1) 62%
	);
	padding: 40px;
	margin: 0;

	.overline {
		margin-bottom: 16px;
	}
`;

const AwardSectionCell = styled(Cell)`
	padding-top: 40px;

	.overline {
		margin-bottom: 16px;
	}
`;

const AwardGrid = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: space-between;
	margin-top: 24px;
`;

const AwardCell = styled.div`
	display: flex;
	background-color: ${colors.white};
	height: 99px;
	width: 99px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 20px;
	overflow: hidden;
	img {
		width: auto;
		max-height: 100px;
	}
`;

const StatBlock = styled.div`
	display: flex;
	align-items: baseline;

	h3 {
		margin-right: 10px;
	}
`;

const HighlightComponent = ({ data }) => {
	const slides = data.testimonialList;
	// Yea, I know
	const statBlock = [
		{
			stat: data.statBlock1.split(' ').shift(),
			description: data.statBlock1.split(' ').splice(1, 2).join(' '),
		},
		{
			stat: data.statBlock2.split(' ').shift(),
			description: data.statBlock2.split(' ').splice(1, 2).join(' '),
		},
		{
			stat: data.statBlock3.split(' ').shift(),
			description: data.statBlock3.split(' ').splice(1, 2).join(' '),
		},
	];
	return (
		<Section
			gradient={
				'45deg, rgba(228,236,251,1) 4%, rgba(248,238,239,1) 26%, rgba(228,236,251,1) 68%, rgba(255,255,255,1) 81%'
			}
		>
			<Grid>
				<AwardSectionCell span={4}>
					<Overline className="overline">AWARDS & RECOGNITION</Overline>
					<h3>A trusted market leader</h3>

					<AwardGrid>
						{data.bottomRowLogoLine.logos.map(logo => {
							return (
								<AwardCell span={6}>
									{logo.gatsbyImageData ? (
										<GatsbyImage image={logo.gatsbyImageData} />
									) : (
										<img src={logo.file.url} />
									)}
								</AwardCell>
							);
						})}
					</AwardGrid>
					<div>
						{statBlock.map(stat => {
							return (
								<StatBlock>
									<Text component="h3">{stat.stat}</Text>
									<Text component="b5">{stat.description}</Text>
								</StatBlock>
							);
						})}
					</div>
				</AwardSectionCell>
				<QuoteCell span={8}>
					<Overline className="overline">WHAT OUR PARTNERS SAY</Overline>
					<h3>Empowering change</h3>
					<Carousel
						slides={slides.map(testimonial => {
							return (
								<div>
									<div>
										<BasicTestimonial
											quote={testimonial.quote.quote}
											bioPhoto={
												testimonial?.quoteAuthor.authorBioPhoto.file.url
											}
											bioName={testimonial?.quoteAuthor.authorName}
											bioTitle={testimonial?.quoteAuthor.authorJobTitle}
											inlineQuotes
										/>
									</div>
									<Button
										style={{ marginTop: '32px' }}
										arrow
										secondary
										to={`/customer-stories/${testimonial.customerStoryPage?.slug}`}
									>
										Read More
									</Button>
								</div>
							);
						})}
						dots
						hideSlideImageAbove="medium"
						id="home-page-slider"
					/>
				</QuoteCell>
			</Grid>
		</Section>
	);
};

const HomepageWrapper = styled.div`
	.new-overline,
	.image-block-overline,
	.overline {
		font-family: ${font.bold};
		font-size: 13px;
		line-height: 13px;
		color: ${colors.newBrand.coolGrey};
		letter-spacing: 0.2em;
		text-transform: uppercase;
	}
	h2 {
		font-family: ${font.recoleta};
		font-size: 44px;
		line-height: 54px;
	}

	h3,
	h5 {
		font-family: ${font.semiBold};
		font-size: 20px;
		line-height: 24px;
		letter-spacing: 0.02em;
		color: ${colors.newBrand.black};
	}

	h3 {
		font-family: ${font.recoleta};
		font-size: 28px;
		line-height: 40px;
	}

	p,
	p:not([class^='Body']):not([class^='AllCaps']) {
		color: ${colors.newBrand.lightGrey};
	}

	.subheader {
		font-family: ${font.regular};
		font-size: 16px;
		line-height: 28px;
		color: ${colors.newBrand.lightGrey};

		${mobileFirstScreenQuery.medium} {
			font-size: 20px;
			line-height: 36px;
		}
	}
`;

const HomeImageBlockContainer = styled(ImageBlockContainer)`
	.imageBlockV2_imageContainer {
		height: 300px;

		${mobileFirstScreenQuery.medium} {
			height: 500px;
		}

		${mobileFirstScreenQuery.large} {
			height: 650px;
		}
	}
`;

const HomepageIcons = styled(IconsA)`
	.grid-container {
		grid-gap 0;
	}

`;

const Page = ({ data, location }) => {
	const content = data.contentfulPageTemplateCustom;
	const heroLogos = content.sections.filter(
		section => section.type === 'ContentfulSectionListOfLogos'
	)[0];

	return (
		<Layout>
			<Helmet
				pageTitle={content.metaTitle}
				description={content.metaDescription}
				location={content.location}
				updatedAt={content.updatedAt}
				schema
			/>
			<Takeover />
			<HomepageWrapper>
				<Hero
					textAlign="left"
					overline={content.heroOverline}
					header={content.heroHeader}
					subheader={content.heroSubheaderLong}
					image={content.heroFeatureImageOrVideo.image.file.url}
					buttons={content.heroButtons}
					noBottomPadding
					showOverflow
					id="homepage-hero"
					heroLogos={heroLogos}
				/>
				<div>
					{content.sections.map(section => {
						if (section.type === 'ContentfulSectionThreeIconsWithLinks') {
							return (
								<>
									<HomepageIcons
										overline={section?.header}
										header={'A social impact platform that’s good for all'}
										subheader={section?.subheader?.childMarkdownRemark.html}
										icon1Description={
											section?.icon1Description?.childMarkdownRemark.html
										}
										icon2Description={
											section?.icon2Description?.childMarkdownRemark.html
										}
										icon3Description={
											section?.icon3Description?.childMarkdownRemark.html
										}
										icon1={section?.icon1?.file.url}
										icon2={section?.icon2?.file.url}
										icon3={section?.icon3?.file.url}
										link1={section?.iconLink1}
										link2={section?.iconLink2}
										link3={section?.iconLink3}
										iconHeight="25px"
									/>
								</>
							);
						}

						if (section.type === 'ContentfulScreenshots') {
							return (
								<LargeScreenshotAccordion
									noTopPadding
									screenshotContent={section}
									overline="END-TO-END SOCIAL IMPACT PLATFORM"
									linkText="See How It Works"
									link="/how-it-works"
									backgroundColor={colors.gradients.accordionBackground}
								/>
							);
						}

						if (section.type === 'ContentfulSectionStackedImageBlocks') {
							return (
								<HomeImageBlockContainer
									containerOverline={section.imageBlockContainerOverline}
									containerHeader={section.imageBlockContainerTitle}
									containerSubheaderHTML={
										section.imageBlockContainerDescription.childMarkdownRemark
											.html
									}
									imageBlocks={section.imageBlockContainerBlocks}
									boxStyle
								/>
							);
						}

						if (section.type === 'ContentfulTestimonialSliders') {
							return <HighlightComponent data={section} />;
						}
					})}
				</div>
				<CTA
					header={content.ctaSection.headline}
					subheader={content.ctaSection.subhead?.childMarkdownRemark?.html}
					button={content.ctaSection.button}
					id="homepage-cta"
				/>
			</HomepageWrapper>
		</Layout>
	);
};

export default Page;

export const homePageQueryV3 = graphql`
	query homePageQueryV3 {
		contentfulPageTemplateCustom(
			contentful_id: { eq: "37J8cIxXcxT5Lr2rjyLNOj" }
		) {
			metaTitle
			metaDescription
			updatedAt
			...CustomHeroFragment
			ctaSection {
				...CTABlockFragment
			}
			sections {
				... on ContentfulSectionListOfLogos {
					type: __typename
					contentful_id
					sectionTitle
					logos {
						contentful_id
						description
						width
						height
						file {
							url
						}
					}
				}
				... on ContentfulSectionThreeIconsWithLinks {
					type: __typename
					...IconsAFragment
				}
				... on ContentfulScreenshots {
					type: __typename
					...ScreenshotsFragment
				}

				... on ContentfulSectionStackedImageBlocks {
					type: __typename
					imageBlockContainerTitle
					imageBlockContainerOverline
					imageBlockContainerDescription {
						childMarkdownRemark {
							html
						}
					}
					imageBlockContainerBlocks {
						... on ContentfulElementImageBlock {
							type: __typename
							contentful_id
							image {
								file {
									url
								}
								gatsbyImageData(
									placeholder: BLURRED
									formats: [AUTO, WEBP, AVIF]
								)
							}
							additionalLinks {
								... on ContentfulElementBasicLink {
									contentful_id
									linkText
									linkUrl
								}
							}
							headerHeadForClPs
							textblockHeader
							textSectionNew {
								raw
								references {
									... on ContentfulStatBlock {
										type: __typename
										contentful_id
										statistic
										statisticDescription {
											childMarkdownRemark {
												html
											}
										}
										statisticIcon {
											file {
												url
											}
										}
										linkText
										linkUrl
									}
								}
							}
						}
					}
				}
				... on ContentfulTestimonialSliders {
					contentful_id
					type: __typename
					secondsBetweenSlides
					statBlock1
					statBlock2
					statBlock3
					testimonialList {
						... on ContentfulPhotoQuote {
							type: __typename
							quote {
								quote
								childMarkdownRemark {
									html
								}
							}
							customerStoryPage {
								slug
							}
							quoteAuthor {
								... on ContentfulElementQuoteAuthor {
									authorName
									authorJobTitle
									authorBioPhoto {
										file {
											url
										}
									}
								}
							}
						}
					}
					bottomRowLogoLine {
						... on ContentfulSectionListOfLogos {
							sectionTitle
							sectionDescription {
								childMarkdownRemark {
									html
								}
							}
							logos {
								file {
									url
								}
							}
						}
					}
				}
			}
		}
	}
`;
